import {setupContentUrl} from 'utils/contentUtils';

const extractCountryAssets = ({ strapiResponse, getUrl, isGridLevel = false }) => {
  const assetMap = new Map();
  let satelliteAsset = null;

  try {
    const countryGrids = strapiResponse?.data?.attributes?.countryGrid || [];

    countryGrids.forEach((grid) => {
      const gridUrl = isGridLevel ? getUrl(grid) : null;

      (grid.countryList || []).forEach((countryItem) => {
        const code = countryItem.countryCode;
        const name = countryItem.countryName;

        const url = gridUrl || getUrl(countryItem);

        if (!url) return;

        if (code) {
          assetMap.set(code.toUpperCase(), url);
        } else if (name?.startsWith('A_')) {
          satelliteAsset = url;
        }
      });
    });
  } catch (error) {
    console.error('Error extracting assets:', error);
  }

  return { assetMap, satelliteAsset };
};

export const extractRoamingBlockFromStrapi = (strapiResponse) => {
  try {
    const roamingBlocks = strapiResponse?.data?.attributes?.roamingBlock;

    const roamingBlock = roamingBlocks?.[0];

    if (!roamingBlock) return null;

    return {
      title: roamingBlock.title || '',
      description: roamingBlock.description || '',
      buttonText: roamingBlock.buttonText || '',
      url: roamingBlock.url || null,
      imageUrl: roamingBlock.image?.data?.attributes?.url || null,
    };
  } catch (error) {
    console.error('Error extracting roaming block:', error);
    return null;
  }
};

export const extractCountryImagesFromStrapi = (strapiResponse) => {
  const { assetMap, satelliteAsset } = extractCountryAssets({
    strapiResponse,
    getUrl: (item) => item.countryImage?.data?.attributes?.url,
    isGridLevel: false,
  });

  return {
    countryImageMap: assetMap,
    satelliteFlag: satelliteAsset,
  };
};

export const extractCountryBannersFromStrapi = (strapiResponse) => {
  const { assetMap, satelliteAsset } = extractCountryAssets({
    strapiResponse,
    getUrl: (grid) => grid.imageGrouping?.data?.attributes?.url,
    isGridLevel: true,
  });

  return {
    countryBannerMap: assetMap,
    satelliteBanner: satelliteAsset,
  };
};

export const extractFlagsAndBannersFromStrapi = (strapiResponse) => {
  const { countryImageMap: flagMap, satelliteFlag } = extractCountryImagesFromStrapi(strapiResponse);

  const { countryBannerMap: bannerMap, satelliteBanner } = extractCountryBannersFromStrapi(strapiResponse);

  const roamingBlock = extractRoamingBlockFromStrapi(strapiResponse);

  return {
    flagMap,
    bannerMap,
    satelliteFlag,
    satelliteBanner,
    roamingBlock,
  };
};

export const isSatelliteCountry = (country) => {
  return !country.iso && (country.el?.startsWith('A_') || country.en?.startsWith('A_'));
};

export const normalizeCountriesData = (countriesData) => {
  if (!Array.isArray(countriesData)) {
    throw new Error('Invalid response format');
  }

  return countriesData.map((country) => ({
    id: country.id,
    iso: country.code,
    en: country.nameEn,
    el: country.nameEl,
    zoneCode: country.zoneCode,
    items: country.items || [],
  }));
};

/**
 * Builds asset objects (flags or banners) for countries
 * Maps country IDs to their asset URLs
 */
export const buildCountryAssets = (countries, assetMap, satelliteAsset) => {
  return countries
    .map((country) => {
      const code = (country.iso || '').toUpperCase();

      let assetUrl = code ? assetMap.get(code) : null;

      if (!assetUrl && isSatelliteCountry(country)) {
        assetUrl = satelliteAsset;
      }

      if (!assetUrl) return null;

      return {
        countryId: country.id,
        src: setupContentUrl(assetUrl),
      };
    })
    .filter(Boolean);
};

/**
 * Sorts countries with satellite zone first, then alphabetically by locale
 * Case-insensitive locale-aware sorting
 */
export const sortCountries = (countries, locale) => {
  if (!countries.length) return [];

  const sorted = [...countries];

  sorted.sort((a, b) => {
    const nameA = locale === 'en' ? a.en || '' : a.el || '';
    const nameB = locale === 'en' ? b.en || '' : b.el || '';

    const isSatelliteA = isSatelliteCountry(a);
    const isSatelliteB = isSatelliteCountry(b);

    if (isSatelliteA && !isSatelliteB) return -1;
    if (!isSatelliteA && isSatelliteB) return 1;

    return nameA.localeCompare(nameB, locale, { sensitivity: 'base' });
  });

  return sorted;
};

export const extractCountryInfo = (countryData, locale) => {
  if (!countryData) {
    return { providersInfo: null, chargesInfo: null, networkInfo: null };
  }

  const providersInfo = countryData.providersInfo || null;

  const chargesInfo = countryData.costs?.[locale]
    ? {
        outgoing: countryData.costs[locale].out,
        incoming: countryData.costs[locale].in,
        sms: countryData.costs[locale].sms,
        data: countryData.costs[locale].data,
      }
    : null;

  const networkInfo =
    Array.isArray(providersInfo) && providersInfo.length > 0
      ? {
          hasVoice: providersInfo.some((provider) => provider.hasVoice),
          hasSms: providersInfo.some((provider) => provider.hasSms),
          hasThirdGen: providersInfo.some((provider) => provider.hasThirdGen),
          hasForthGen: providersInfo.some((provider) => provider.hasForthGen),
          hasFifthGen: providersInfo.some((provider) => provider.hasFifthGen),
        }
      : null;

  return { providersInfo, chargesInfo, networkInfo };
};
